import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface DateRangeFilterProps {
  /** From date in YYYY-MM-DD format */
  dateFrom: string;
  /** To date in YYYY-MM-DD format */
  dateTo: string;
  /** Called when user clicks Apply or Clear; values in YYYY-MM-DD */
  onApply: (dateFrom: string, dateTo: string) => void;
  /** Optional label override (defaults to i18n 'dateRangeFilter.label') */
  label?: string;
  /** Disabled state */
  disabled?: boolean;
}

const inputStyle: React.CSSProperties = {
  background: 'rgba(255, 255, 255, 0.08)',
  border: '1px solid rgba(255, 255, 255, 0.15)',
  borderRadius: '4px',
  color: '#e2e8f0',
  padding: '2px 6px',
  fontSize: '13px',
  height: '28px',
  colorScheme: 'dark',
};

const buttonStyle: React.CSSProperties = {
  background: 'rgba(255, 255, 255, 0.1)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: '4px',
  color: '#e2e8f0',
  padding: '2px 10px',
  fontSize: '12px',
  height: '28px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  dateFrom,
  dateTo,
  onApply,
  label,
  disabled = false,
}) => {
  const { t } = useTranslation();

  // Local state for the inputs — only pushed to parent on Apply/Clear
  const [localFrom, setLocalFrom] = useState(dateFrom);
  const [localTo, setLocalTo] = useState(dateTo);

  // Sync local state when parent changes (e.g. initial defaults set)
  useEffect(() => {
    setLocalFrom(dateFrom);
    setLocalTo(dateTo);
  }, [dateFrom, dateTo]);

  const displayLabel = label || t('dateRangeFilter.label');
  const hasFilter = dateFrom || dateTo;
  const hasLocalChanges = localFrom !== dateFrom || localTo !== dateTo;

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
      <span style={{ color: '#fff', fontSize: '14px', whiteSpace: 'nowrap' }}>
        {displayLabel}:
      </span>
      <input
        type="date"
        value={localFrom}
        onChange={(e) => setLocalFrom(e.target.value)}
        disabled={disabled}
        style={inputStyle}
      />
      <span style={{ color: '#94a3b8', fontSize: '12px' }}>–</span>
      <input
        type="date"
        value={localTo}
        onChange={(e) => setLocalTo(e.target.value)}
        disabled={disabled}
        style={inputStyle}
      />
      {hasLocalChanges && (
        <button
          onClick={() => onApply(localFrom, localTo)}
          disabled={disabled}
          style={buttonStyle}
        >
          OK
        </button>
      )}
      {hasFilter && !hasLocalChanges && (
        <button
          onClick={() => { setLocalFrom(''); setLocalTo(''); onApply('', ''); }}
          disabled={disabled}
          title={t('dateRangeFilter.showAll')}
          style={{
            background: 'none',
            border: 'none',
            color: '#94a3b8',
            cursor: 'pointer',
            fontSize: '16px',
            padding: '0 4px',
            lineHeight: 1,
          }}
        >
          ×
        </button>
      )}
    </div>
  );
};

export default DateRangeFilter;
